import React, { useEffect, useRef } from 'react';
import { MESSAGE_TYPES } from '@sv-support-sdk/utils/src/constants/chatMessages';
import List from '@sv-support-sdk/ui-components/src/components/Lists/List';
import Divider from '@sv-support-sdk/ui-components/src/components/Content/Divider';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { chatMessages, typingSelector } from '../../store/selectors/messages';
import MessageText from './messageTypes/MessageText';
import Ticket from './messageTypes/Ticket';
import CreatedTicket from './messageTypes/CreatedTicket';
import Notification from './messageTypes/Notification';
import { readMessage } from '../../store/actions/messages';
import Feedback from './messageTypes/Feedback';
import AskEmail from './messageTypes/AskEmail';
import QuickActions from './messageTypes/QuickActions';
import ChatIsClosed from './messageTypes/ChatIsClosed';
import TypingBlock from './messageTypes/TypingBlock';
import SystemDescription from './messageTypes/SystemDescription';
import Callback from './messageTypes/Callback';
import { MESSAGE_ID_NAME } from '../../constants/messages';

const MessagesList = () => {
  const messages = useAppSelector(chatMessages);
  const messagesListRef = useRef<HTMLUListElement | null>(null);
  const typing = useAppSelector(typingSelector);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];

    if (!lastMessage || !messagesListRef.current) return;
    messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight * 2;
    if (!window.document.hidden) {
      dispatch(readMessage());
    }
  }, [messages, typing]);

  useEffect(() => {
    if (messagesListRef.current) {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }

    if (!window.document.hidden) {
      dispatch(readMessage());
    }
  }, []);

  return (
    <>
      <List withSubChildren indents="16" gap="16" overflow="auto" ref={messagesListRef}>
        {messages.map((message) => {
          switch (message.type) {
            case MESSAGE_TYPES.OPEN:
            case MESSAGE_TYPES.JOIN:
            case MESSAGE_TYPES.MESSAGE:
            case MESSAGE_TYPES.ATTACHMENT:
            case MESSAGE_TYPES.TRANSFER:
              return <MessageText key={message.id || message.uuid} message={message} />;
            case MESSAGE_TYPES.TICKET:
              return <Ticket key={message.id || message.uuid || 'ticket'} />;
            case MESSAGE_TYPES.TICKET_CREATED:
              return (
                <CreatedTicket
                  key={message.id || message.uuid}
                  id={message.id}
                  hash={message.hash}
                />
              );
            case MESSAGE_TYPES.NOTIFICATION:
              return (
                <Notification
                  key={message.id || message.uuid}
                  title={message.title}
                  message={message.message}
                  icon={message.icon}
                  id={`${MESSAGE_ID_NAME}${message.id}`}
                />
              );
            case MESSAGE_TYPES.ASK_EMAIL:
              return <AskEmail key={message.id || message.uuid} />;
            case MESSAGE_TYPES.DIVIDER:
              return <Divider key={message.id || message.uuid} text={message.message} />;
            case MESSAGE_TYPES.FEEDBACK:
              return <Feedback key={message.id || message.uuid} message={message} />;
            case MESSAGE_TYPES.QUICK_ACTIONS:
              return <QuickActions key={message.id || message.uuid} message={message} />;
            case MESSAGE_TYPES.CHAT_IS_CLOSED:
              return <ChatIsClosed key={message.id || message.uuid} />;
            case MESSAGE_TYPES.CALLBACK:
              return <Callback key={message.id || message.uuid} message={message} />;
            case MESSAGE_TYPES.SYSTEM:
              return (
                <SystemDescription key={message.id || message.uuid} message={message.message} />
              );
            default:
              return null;
          }
        })}
        {typing && <TypingBlock supportTyping={typing} />}
      </List>
    </>
  );
};

export default MessagesList;
